/**
 * What happens to a container whose display content the layer removed.
 *
 * Removing a decorative marker can empty the node that held it. The right
 * answer depends entirely on what the container is for: a paragraph that now
 * shows nothing should go, a table cell must stay or the grid shifts, and a
 * link whose whole label was consumed has lost an affordance the reader needs
 * — that transform has to be abandoned, not tidied up.
 *
 * Both functions are pure decisions. Acting on them belongs to the pass that
 * owns the tree.
 */
import {
    classifyEditorialCapabilities,
    type EditorialHastNode,
} from "./editorial-capabilities";
import { isInlineWrapperTag } from "./editorial-inline-run";
import { getEditorialProvenance } from "./editorial-provenance";
// The anchor pass owns this name; importing it keeps one spelling rather than a
// third private copy of the reserved namespace.
import { STREAM_TAIL_PROPERTY } from "./rehype-stream-tail-anchor";

export type EditorialContainerOutcome =
    | "suppress"
    | "retain"
    | "retain-empty"
    | "rollback"
    | "unchanged";

export interface EditorialContainerState {
    /** The container renders no display text once the plan is applied. */
    readonly displayEmpty: boolean;
    /** Something other than display text survives — a nested list, an image. */
    readonly hasSubstantiveChild: boolean;
    /**
     * This container is an item of an ORDERED list. Optional, and absent means
     * "not ordered", so a caller that cannot see the parent stays on the
     * conservative path it had before.
     */
    readonly inOrderedList?: boolean;
}

// Containers that exist to hold display content, so an empty one is noise.
const SUPPRESSIBLE_TAGS: ReadonlySet<string> = new Set([
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
]);

// A cell is positional: dropping it shifts every cell after it into the wrong
// column, so an emptied one stays as an empty cell.
const CELL_TAGS: ReadonlySet<string> = new Set(["td", "th"]);

export const resolveContainerOutcome = (
    node: EditorialHastNode,
    state: EditorialContainerState
): EditorialContainerOutcome => {
    if (!state.displayEmpty) return "unchanged";

    const tagName = node.tagName ?? "";
    if (CELL_TAGS.has(tagName)) return "retain-empty";

    const { contract } = classifyEditorialCapabilities(node);
    if (
        contract === "verbatim" ||
        contract === "protocol" ||
        contract === "source-model"
    ) {
        // Nothing inside these was ours to change, so there is no outcome to
        // decide — reaching here at all means the caller measured emptiness
        // that this layer did not cause.
        return "unchanged";
    }

    // An emptied link still occupies the tab order and still navigates, but now
    // announces nothing. Undoing the transform is the only outcome that leaves
    // the reader with what the author wrote.
    if (contract === "interactive") return "rollback";

    if (SUPPRESSIBLE_TAGS.has(tagName)) {
        if (state.hasSubstantiveChild) return "retain";

        // An ordered list numbers by position, so dropping an emptied item
        // renumbers every item after it — the reader is shown different
        // numbers than the author wrote, and any prose referring to "step 4"
        // now points at the wrong step. Positional exactly like a cell.
        if (tagName === "li" && state.inOrderedList) return "retain-empty";

        return "suppress";
    }

    // Unrecognized container: leaving authored markup alone beats deleting
    // something whose purpose this layer cannot read.
    return "unchanged";
};

/**
 * A property the author put there, as opposed to one this layer wrote.
 *
 * The tail anchor marks an EXISTING source node, so its attribute lands on
 * markup the author owns. Counting it as authored let this layer's own mark
 * protect debris from pruning: an emptied `<strong>` that happened to be
 * selected as the tail became unprunable BECAUSE it was marked.
 *
 * Only the tail mark is discounted, and only with the provenance to back it.
 * A glyph span also carries a layer-minted property, but it is not an emptied
 * wrapper — it IS content, and discounting its property would make the layer
 * prune the glyphs it just produced.
 */
const isLayerTailMark = (node: EditorialHastNode, name: string): boolean =>
    name === STREAM_TAIL_PROPERTY &&
    getEditorialProvenance(node)?.kind === "tail-anchor";

const hasAuthoredProperty = (node: EditorialHastNode): boolean =>
    Object.keys(node.properties ?? {}).some(
        (name) => !isLayerTailMark(node, name)
    );

/**
 * A formatting wrapper carrying nothing of its own, so it is worth keeping
 * only for what it contains. Says nothing about whether it is empty *now*: the
 * plan projection asks this of a wrapper that is ABOUT to be emptied, which is
 * the only caller there is.
 *
 * `id`, `className` and the status attributes are all reasons the author put
 * the element there: an empty `<strong id="x">` is still a link target.
 */
export const isDisposableWrapper = (node: EditorialHastNode): boolean =>
    node.type === "element" &&
    isInlineWrapperTag(node.tagName ?? "") &&
    !hasAuthoredProperty(node);
